import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../components/AppIcon";
import { useAuth } from "../context/AuthContext";
import {
  listPlacementSessions,
  listStudents,
  getInstitutionCredits,
  listCampaigns,
  getAttention,
  getAnalyticsOverview,
} from "../api/client";
import { getToken } from "../api/client";
import {
  readInstitutionDashboardCache,
  writeInstitutionDashboardCache,
} from "../api/dashboard-cache";

function StatCard({ icon, label, value, hint, onClick }) {
  const clickable = typeof onClick === "function";
  return (
    <div
      className={
        "rounded-xl border border-border bg-card p-5 " +
        (clickable ? "cursor-pointer transition-colors hover:bg-muted/40" : "")
      }
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{label}</span>
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Icon name={icon} size={16} />
        </span>
      </div>
      <div className="mt-3 text-2xl font-semibold text-foreground">{value}</div>
      {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

function ChecklistItem({ done, title, description, actionLabel, onAction }) {
  return (
    <li className="flex items-start gap-3">
      <span
        className={
          "mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border " +
          (done
            ? "border-primary bg-primary text-primary-foreground"
            : "border-border bg-background text-transparent")
        }
      >
        <Icon name="Check" size={12} />
      </span>
      <div className="min-w-0">
        <div className="text-sm font-medium text-foreground">{title}</div>
        <div className="text-sm text-muted-foreground">{description}</div>
        {!done && actionLabel && onAction && (
          <button
            onClick={onAction}
            className="mt-1.5 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
          >
            {actionLabel}
            <Icon name="ArrowRight" size={13} />
          </button>
        )}
      </div>
    </li>
  );
}

// ── Needs attention ─────────────────────────────────────────────────────────
// The dashboard's primary card once setup is done: the students to meet with
// because of poor performance, ranked weakest-average-first, over
// GET /api/institution/attention. The backend owns the "at-risk" definition
// (below the pass bar, or a declining trend) and the ranking; this component
// owns presentation. A compact cohort score-distribution graph (buckets from
// the analytics overview) sits on top so the admin sees the shape of the cohort
// before the list. Every row drills into the student's detail page — no
// transcripts, same privacy model as the rest of the console.

// scoreTone colors a score by the platform's pass bands (≥75 strong, ≥60 pass).
const scoreTone = (s) => {
  if (s == null) return "text-muted-foreground";
  const n = Number(s);
  if (n >= 75) return "text-success";
  if (n >= 60) return "text-warning";
  return "text-destructive";
};

const TREND_CHIP = {
  improving: { icon: "TrendingUp", cls: "bg-success/15 text-success" },
  declining: { icon: "TrendingDown", cls: "bg-destructive/10 text-destructive" },
  flat: { icon: "Minus", cls: "bg-muted text-muted-foreground" },
  na: { icon: "Minus", cls: "bg-muted text-muted-foreground" },
};

// ScoreBars is a small dependency-free bar chart of completed interviews by
// score band. Bands echo the pass colors (low = red, mid = amber, high = green)
// so a left-heavy chart reads as "cohort is struggling" at a glance.
function ScoreBars({ buckets }) {
  const max = buckets.reduce((m, b) => Math.max(m, b.count || 0), 0);
  if (max <= 0) return null;
  return (
    <div className="flex h-24 items-end gap-2">
      {buckets.map((b) => {
        const h = Math.max(((b.count || 0) / max) * 100, b.count > 0 ? 6 : 0);
        const tone =
          b.min >= 61 ? "bg-success/80" : b.min >= 41 ? "bg-warning/80" : "bg-destructive/70";
        return (
          <div key={b.bucket} className="flex flex-1 flex-col items-center gap-1">
            <span className="text-[11px] font-medium text-foreground">{b.count || 0}</span>
            <div className="flex w-full flex-1 items-end">
              <div className={"w-full rounded-t " + tone} style={{ height: `${h}%` }} />
            </div>
            <span className="text-[10px] text-muted-foreground">{b.bucket}</span>
          </div>
        );
      })}
    </div>
  );
}

function NeedsAttention({ loading, error, data, dist, hasActivity, navigate }) {
  const students = data?.students || [];
  const total = data?.total || 0;
  const passThreshold = data?.passThreshold ?? 60;
  const buckets = Array.isArray(dist) ? dist : [];
  const hasScores = buckets.some((b) => (b.count || 0) > 0);

  const header = (
    <div className="flex items-center gap-2">
      <Icon name="AlertTriangle" size={18} className="text-warning" />
      <h2 className="text-base font-semibold text-foreground">Needs attention</h2>
      {total > 0 && (
        <span className="ml-auto rounded-full bg-warning/10 px-2.5 py-0.5 text-xs font-semibold text-warning">
          {total}
        </span>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="rounded-xl border border-border bg-card p-6">
        {header}
        <div className="mt-5 space-y-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-12 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-border bg-card p-6">
        {header}
        <p className="mt-4 text-sm text-muted-foreground">
          Couldn&apos;t load attention items right now.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-card p-6">
      {header}
      <p className="mt-1 text-sm text-muted-foreground">
        Students to meet with — weakest average first (below {passThreshold}, or trending down).
      </p>

      {/* Cohort score distribution — the shape of the cohort before the list */}
      {hasScores && (
        <div className="mt-5">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Score distribution
          </span>
          <div className="mt-2">
            <ScoreBars buckets={buckets} />
          </div>
        </div>
      )}

      {/* Ranked at-risk list */}
      {total === 0 ? (
        !hasActivity ? null : (
          <div className="mt-5 flex items-center gap-2 rounded-lg bg-success/5 px-3 py-3 text-sm text-muted-foreground">
            <Icon name="CheckCircle2" size={16} className="text-success" />
            {hasScores
              ? `No students are below ${passThreshold} or trending down — scores look healthy.`
              : "No completed interviews yet — this fills in as students finish."}
          </div>
        )
      ) : (
        <ul className="mt-5 space-y-1">
          {students.map((s, i) => {
            const meta = TREND_CHIP[s.trend] || TREND_CHIP.na;
            const showDelta = s.trend === "improving" || s.trend === "declining";
            return (
              <li key={s.studentId || i}>
                <button
                  type="button"
                  onClick={() => s.studentId && navigate(`/institution/students/${s.studentId}`)}
                  className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors hover:bg-muted/50"
                >
                  <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground">
                    {i + 1}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-foreground">{s.name}</span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {[s.batchName, `${s.completed} completed`].filter(Boolean).join(" · ")}
                    </span>
                  </span>
                  {showDelta && (
                    <span
                      className={
                        "hidden flex-shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium sm:inline-flex " +
                        meta.cls
                      }
                    >
                      <Icon name={meta.icon} size={12} />
                      {s.trendDelta > 0 ? "+" : ""}
                      {s.trendDelta}
                    </span>
                  )}
                  <span className={"w-11 flex-shrink-0 text-right text-sm font-semibold " + scoreTone(s.averageScore)}>
                    {s.averageScore == null ? "—" : Number(s.averageScore).toFixed(1)}
                  </span>
                  <Icon name="ChevronRight" size={14} className="flex-shrink-0 text-muted-foreground" />
                </button>
              </li>
            );
          })}
          {total > students.length && (
            <li>
              <button
                type="button"
                onClick={() => navigate("/institution/results")}
                className="mt-1 inline-flex items-center gap-1 pl-2 text-xs font-medium text-primary hover:underline"
              >
                +{total - students.length} more in results <Icon name="ArrowRight" size={12} />
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}


// Shared style for the dashboard header shortcut buttons (moved out of the
// Needs-attention card so that panel stays a clean list of students).
const quickActionCls =
  "inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-muted/50";

export default function Dashboard() {
  const { user, institution } = useAuth();
  const navigate = useNavigate();
  const firstName = (user?.name || "there").split(/\s+/)[0];

  const [batchCount, setBatchCount] = useState(null);
  const [studentCount, setStudentCount] = useState(null);
  const [creditsRemaining, setCreditsRemaining] = useState(null);
  const [campaignCount, setCampaignCount] = useState(null);
  const [activeCampaignCount, setActiveCampaignCount] = useState(null);
  const [assignedTotal, setAssignedTotal] = useState(null);
  const [completedTotal, setCompletedTotal] = useState(null);

  const [attn, setAttn] = useState(null);
  const [dist, setDist] = useState(null);
  const [attnLoading, setAttnLoading] = useState(true);
  const [attnError, setAttnError] = useState(false);

  useEffect(() => {
    let alive = true;

    const applySnapshot = (snapshot, isNetworkResult = false) => {
      const headline = snapshot?.headline;
      if (headline) {
        const { batches = [], students = {}, credits = {}, campaigns = [] } = headline;
        setBatchCount(batches.length);
        setStudentCount(students.total || 0);
        setCreditsRemaining(credits.creditsRemaining ?? 0);
        setCampaignCount(campaigns.length);
        setActiveCampaignCount(campaigns.filter((c) => c.status === "active").length);
        setAssignedTotal(campaigns.reduce((sum, c) => sum + (c.assignedCount || 0), 0));
        setCompletedTotal(campaigns.reduce((sum, c) => sum + (c.completedCount || 0), 0));
      } else if (isNetworkResult) {
        setBatchCount(0);
        setStudentCount(0);
        setCreditsRemaining(0);
        setCampaignCount(0);
        setActiveCampaignCount(0);
        setAssignedTotal(0);
        setCompletedTotal(0);
      }

      if (snapshot?.attention) {
        setAttn(snapshot.attention);
        setAttnError(false);
      } else if (isNetworkResult) {
        setAttnError(true);
      }
      if (snapshot?.overview) setDist(snapshot.overview.scoreDistribution || []);
      if (snapshot?.attention || snapshot?.overview || isNetworkResult) setAttnLoading(false);
    };

    (async () => {
      const token = getToken();
      let freshApplied = false;
      const cachedSnapshot = readInstitutionDashboardCache(token);
      const freshSnapshot = Promise.allSettled([
        Promise.all([
          listPlacementSessions(),
          listStudents({ limit: 1, offset: 0 }),
          getInstitutionCredits(),
          listCampaigns(),
        ]),
        getAttention(),
        getAnalyticsOverview(),
      ]);

      // Render IndexedDB data as soon as it is ready, while all APIs above are
      // already running in parallel. Fresh network data wins if it arrives first.
      cachedSnapshot.then((snapshot) => {
        if (alive && !freshApplied && snapshot) applySnapshot(snapshot);
      });

      const [headlineRes, attentionRes, overviewRes] = await freshSnapshot;
      if (!alive) return;
      freshApplied = true;
      const snapshot = {
        headline: headlineRes.status === "fulfilled"
          ? {
              batches: headlineRes.value[0],
              students: headlineRes.value[1],
              credits: headlineRes.value[2],
              campaigns: headlineRes.value[3],
            }
          : null,
        attention: attentionRes.status === "fulfilled" ? attentionRes.value : null,
        overview: overviewRes.status === "fulfilled" ? overviewRes.value : null,
      };
      applySnapshot(snapshot, true);
      if (snapshot.headline && snapshot.attention && snapshot.overview) {
        void writeInstitutionDashboardCache(token, snapshot);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const hasBatch = (batchCount ?? 0) > 0;
  const hasStudent = (studentCount ?? 0) > 0;
  const hasCampaign = (campaignCount ?? 0) > 0;
  const hasAssignment = (assignedTotal ?? 0) > 0;
  // Once every setup step is done, the checklist is dead weight — swap it for
  // the "Needs attention" panel (students to meet, by performance) as the
  // dashboard's primary card, and surface the shortcut actions in the header.
  const onboardingComplete = hasBatch && hasStudent && hasCampaign && hasAssignment;
  const completionRate =
    assignedTotal && assignedTotal > 0
      ? Math.round((completedTotal / assignedTotal) * 100)
      : null;

  return (
    <div className="mx-auto max-w-6xl space-y-8">
      {/* Greeting + shortcut actions (actions live here, not inside the panel) */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            Welcome, {firstName} 👋
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Here&apos;s an overview of {institution?.name || "your institution"}&apos;s
            placement preparation.
          </p>
        </div>
        {onboardingComplete && (
          <div className="flex flex-wrap gap-2">
            <button type="button" className={quickActionCls} onClick={() => navigate("/institution/results")}>
              <Icon name="BarChart3" size={14} className="text-muted-foreground" />
              View results
            </button>
            <button type="button" className={quickActionCls} onClick={() => navigate("/institution/campaigns")}>
              <Icon name="Send" size={14} className="text-muted-foreground" />
              Assign a campaign
            </button>
            <button type="button" className={quickActionCls} onClick={() => navigate("/institution/students")}>
              <Icon name="Users" size={14} className="text-muted-foreground" />
              Manage students
            </button>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon="Users"
          label="Students"
          value={studentCount == null ? "…" : studentCount}
          hint={hasStudent ? "Across all batches" : "No students added yet"}
          onClick={() => navigate("/institution/students")}
        />
        <StatCard
          icon="Send"
          label="Active campaigns"
          value={activeCampaignCount == null ? "…" : activeCampaignCount}
          hint={
            campaignCount
              ? `${campaignCount} campaign${campaignCount === 1 ? "" : "s"} total`
              : "No campaigns yet"
          }
          onClick={() => navigate("/institution/campaigns")}
        />
        <StatCard
          icon="Wallet"
          label="Credits remaining"
          value={creditsRemaining == null ? "…" : creditsRemaining}
          hint={creditsRemaining ? "Available across batches" : "Allocate a credit pool"}
          onClick={() => navigate("/institution/credits")}
        />
        <StatCard
          icon="CheckCircle2"
          label="Completion rate"
          value={
            assignedTotal == null ? "…" : completionRate == null ? "—" : `${completionRate}%`
          }
          hint={
            hasAssignment
              ? `${completedTotal} of ${assignedTotal} completed`
              : "Awaiting first results"
          }
          onClick={hasAssignment ? () => navigate("/institution/results") : undefined}
        />
      </div>

      {/* Primary column swaps with setup progress: onboarding checklist while
          steps remain, then the operational "Needs attention" panel once done. */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          {onboardingComplete ? (
            <NeedsAttention
              loading={attnLoading}
              error={attnError}
              data={attn}
              dist={dist}
              hasActivity={hasAssignment}
              navigate={navigate}
            />
          ) : (
            <div className="rounded-xl border border-border bg-card p-6">
              <div className="flex items-center gap-2">
                <Icon name="Rocket" size={18} className="text-primary" />
                <h2 className="text-base font-semibold text-foreground">
                  Get started
                </h2>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                Follow these steps to run your first interview campaign. We&apos;ll
                unlock each step as the console rolls out.
              </p>
              <ul className="mt-6 space-y-5">
                <ChecklistItem
                  done
                  title="Create your institution account"
                  description="You're all set — your placement cell console is ready."
                />
                <ChecklistItem
                  done={hasBatch}
                  title="Create a placement batch"
                  description="Group students by graduating year or drive, and set a student cap."
                  actionLabel="Create a batch"
                  onAction={() => navigate("/institution/batches")}
                />
                <ChecklistItem
                  done={hasStudent}
                  title="Add or import students"
                  description="Add students one by one or upload a CSV roster."
                  actionLabel="Add students"
                  onAction={() => navigate("/institution/students")}
                />
                <ChecklistItem
                  done={hasCampaign}
                  title="Design an interview campaign"
                  description="Pick the role, type, difficulty and topics once — reuse it for everyone."
                  actionLabel="Create a campaign"
                  onAction={() => navigate("/institution/campaigns")}
                />
                <ChecklistItem
                  done={hasAssignment}
                  title="Assign & track"
                  description="Assign to your cohort and watch completion and scores roll in."
                  actionLabel="Assign a campaign"
                  onAction={() => navigate("/institution/campaigns")}
                />
              </ul>
            </div>
          )}
        </div>

        {/* Account summary */}
        <div className="rounded-xl border border-border bg-card p-6">
          <div className="flex items-center gap-2">
            <Icon name="Building2" size={18} className="text-primary" />
            <h2 className="text-base font-semibold text-foreground">Your institution</h2>
          </div>
          <dl className="mt-5 space-y-4 text-sm">
            <div>
              <dt className="text-muted-foreground">Name</dt>
              <dd className="mt-0.5 font-medium text-foreground">
                {institution?.name || "—"}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Your role</dt>
              <dd className="mt-0.5 font-medium capitalize text-foreground">
                {institution?.role || "admin"}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Status</dt>
              <dd className="mt-0.5">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-success/15 px-2.5 py-0.5 text-xs font-medium capitalize text-success">
                  <span className="h-1.5 w-1.5 rounded-full bg-success" />
                  {institution?.status || "active"}
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Signed in as</dt>
              <dd className="mt-0.5 font-medium text-foreground">{user?.email || "—"}</dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  );
}
